import UploadFileIcon from "@mui/icons-material/UploadFile";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import FinancialCoreApi from "../../../../../infrastructure/api/financialCore";

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

const formatMoney = (value) => {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("fa-IR");
};

const PARTNER_OPTIONS = [
  { value: "BARQ_PAY", label: "برق‌پی (BARQ_PAY)" },
  { value: "IRIA_PAY", label: "ایریا (IRIA_PAY)" },
];

const normalizeImportResult = (raw) => {
  if (!raw) return null;
  const rows = pick(raw, "rows", "Rows") || [];
  return {
    confirm: pick(raw, "confirm", "Confirm"),
    success: pick(raw, "success", "Success"),
    message: pick(raw, "message", "Message") || "",
    totalRows: pick(raw, "totalRows", "TotalRows") ?? 0,
    matchedCount: pick(raw, "matchedCount", "MatchedCount") ?? 0,
    createdCount: pick(raw, "createdCount", "CreatedCount") ?? 0,
    skippedCount: pick(raw, "skippedCount", "SkippedCount") ?? 0,
    errorCount: pick(raw, "errorCount", "ErrorCount") ?? 0,
    fileTotalAmount: pick(raw, "fileTotalAmount", "FileTotalAmount") ?? 0,
    totalAmount: pick(raw, "totalAmount", "TotalAmount") ?? 0,
    sharedDocumentNumber:
      pick(raw, "sharedDocumentNumber", "SharedDocumentNumber") || "",
    rows: (Array.isArray(rows) ? rows : []).map((r) => ({
      excelRowNumber: pick(r, "excelRowNumber", "ExcelRowNumber"),
      requestNumberRaw: pick(r, "requestNumberRaw", "RequestNumberRaw") || "",
      amount: pick(r, "amount", "Amount"),
      documentNumber: pick(r, "documentNumber", "DocumentNumber") || "",
      requestInfoId: pick(r, "requestInfoId", "RequestInfoId"),
      requestReferenceId: pick(r, "requestReferenceId", "RequestReferenceId"),
      loanProcessId: pick(r, "loanProcessId", "LoanProcessId"),
      paymentTransactionId: pick(
        r,
        "paymentTransactionId",
        "PaymentTransactionId"
      ),
      outcome: pick(r, "outcome", "Outcome") || "",
      message: pick(r, "message", "Message") || "",
    })),
  };
};

const outcomeColor = (outcome) => {
  switch (outcome) {
    case "Created":
    case "WouldCreate":
      return "success";
    case "AlreadyRegistered":
    case "Skipped":
    case "NotFound":
      return "warning";
    case "Error":
      return "error";
    default:
      return "default";
  }
};

const outcomeLabel = (outcome) => {
  switch (outcome) {
    case "WouldCreate":
      return "قابل ثبت";
    case "Created":
      return "ثبت شد";
    case "AlreadyRegistered":
      return "قبلاً ثبت";
    case "NotFound":
      return "یافت نشد";
    case "Skipped":
      return "رد شد";
    case "Error":
      return "خطا";
    default:
      return outcome || "—";
  }
};

/**
 * @param {{ open: boolean, source?: string, defaultPartnerCode?: string, onClose: () => void, onCommitted?: () => void }} props
 */
const LoanDisbursementExcelImportDialog = ({
  open,
  source = "SolarentOperator",
  defaultPartnerCode = "",
  onClose,
  onCommitted,
}) => {
  const fileInputRef = useRef(null);
  const selectedFileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [partnerCode, setPartnerCode] = useState(defaultPartnerCode || "");

  useEffect(() => {
    if (open) {
      setPartnerCode(defaultPartnerCode || "");
    }
  }, [open, defaultPartnerCode]);

  const hasFile = Boolean(selectedFileRef.current) || Boolean(fileName);
  const canApply =
    Boolean(result) &&
    !result.confirm &&
    result.rows?.some((r) => r.outcome === "WouldCreate");

  const summaryChips = useMemo(() => {
    if (!result) return [];
    return [
      { label: `کل: ${result.totalRows}`, color: "default" },
      {
        label: `قابل ثبت: ${result.rows.filter((r) => r.outcome === "WouldCreate").length}`,
        color: "success",
      },
      { label: `ثبت‌شده: ${result.createdCount}`, color: "success" },
      { label: `ردشده: ${result.skippedCount}`, color: "warning" },
      { label: `خطا: ${result.errorCount}`, color: "error" },
      {
        label: `جمع فایل: ${formatMoney(result.fileTotalAmount ?? result.totalAmount)}`,
        color: "default",
      },
      {
        label: `جمع قابل ثبت: ${formatMoney(result.totalAmount)}`,
        color: "info",
      },
    ];
  }, [result]);

  const resetState = () => {
    selectedFileRef.current = null;
    setFileName("");
    setResult(null);
    setLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose?.();
  };

  const handleFileChange = (e) => {
    const next = e.target.files?.[0] || null;
    selectedFileRef.current = next;
    setFileName(next?.name || "");
    setResult(null);
  };

  const runImport = async (confirm) => {
    const file =
      selectedFileRef.current || fileInputRef.current?.files?.[0] || null;
    if (!file) {
      toast.error("ابتدا فایل اکسل را انتخاب کنید.");
      return;
    }
    if (!partnerCode) {
      toast.error("لندتک فایل را انتخاب کنید (برق‌پی یا ایریا).");
      return;
    }
    selectedFileRef.current = file;
    setFileName(file.name || "");
    setLoading(true);
    try {
      const res = await FinancialCoreApi.importLoanDisbursementsExcel(
        source,
        file,
        confirm,
        partnerCode
      );
      if (res?.isSuccess === false) {
        toast.error(
          res?.message || res?.Message || "خطا در ایمپورت اکسل واریز وام"
        );
        return;
      }
      const data = normalizeImportResult(res?.data ?? res?.Data ?? res);
      setResult(data);
      if (confirm && data?.createdCount > 0) {
        toast.success(data?.message || "واریزهای وام ثبت شد");
        onCommitted?.();
      }
    } catch (e) {
      const apiMsg = e?.response?.data?.message || e?.response?.data?.Message;
      toast.error(apiMsg || e?.message || "خطا در ایمپورت اکسل واریز وام");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>آپلود اکسل تفکیک واریز وام تجمیعی</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          برای واریز نهایی ایریا یا برق‌پی، فایل اکسل با ستون‌های{" "}
          <strong>شماره درخواست</strong>، <strong>مبلغ واریزی</strong> (ریال)،{" "}
          <strong>شماره سند</strong> / <strong>سند واریزی</strong> را آپلود کنید.
          <strong> انتخاب لندتک الزامی است</strong> تا واریز به وام لندتک دیگر
          ثبت نشود. ابتدا پیش‌نمایش بگیرید، سپس ثبت کنید.
        </Alert>

        <FormControl size="small" fullWidth required sx={{ mb: 2, maxWidth: 360 }}>
          <InputLabel id="loan-disb-excel-partner">لندتک فایل *</InputLabel>
          <Select
            labelId="loan-disb-excel-partner"
            label="لندتک فایل *"
            value={partnerCode}
            onChange={(e) => {
              setPartnerCode(e.target.value);
              setResult(null);
            }}
            disabled={loading}
            displayEmpty
            error={!partnerCode}
          >
            <MenuItem value="" disabled>
              انتخاب کنید…
            </MenuItem>
            {PARTNER_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={loading}
          >
            انتخاب فایل
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" color="text.secondary">
            {fileName || "فایلی انتخاب نشده (.xlsx)"}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            disabled={!hasFile || !partnerCode || loading}
            onClick={() => runImport(false)}
          >
            پیش‌نمایش
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<SaveIcon />}
            disabled={!canApply || !partnerCode || loading}
            onClick={() => runImport(true)}
          >
            ثبت واریزها
          </Button>
        </Stack>

        {result?.sharedDocumentNumber ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            شماره سند مشترک: {result.sharedDocumentNumber}
          </Alert>
        ) : null}

        {result ? (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {summaryChips.map((c) => (
                <Chip
                  key={c.label}
                  size="small"
                  label={c.label}
                  color={c.color}
                />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {result.message}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ردیف</TableCell>
                  <TableCell>شماره درخواست</TableCell>
                  <TableCell align="left">مبلغ</TableCell>
                  <TableCell>شماره سند</TableCell>
                  <TableCell>وضعیت</TableCell>
                  <TableCell>پیام</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.rows.map((r) => (
                  <TableRow key={`${r.excelRowNumber}-${r.requestNumberRaw}`}>
                    <TableCell>{r.excelRowNumber}</TableCell>
                    <TableCell>
                      {r.requestReferenceId || r.requestNumberRaw || "—"}
                    </TableCell>
                    <TableCell align="left">{formatMoney(r.amount)}</TableCell>
                    <TableCell>{r.documentNumber || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={outcomeColor(r.outcome)}
                        label={outcomeLabel(r.outcome)}
                      />
                    </TableCell>
                    <TableCell>{r.message || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          بستن
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoanDisbursementExcelImportDialog;
